import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Eye, EyeOff, AlertCircle, Loader2 } from "lucide-react";
import Logo from "../components/app/Logo";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setError(""); setLoading(true);
    try {
      await signIn(email.trim(), password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-md mx-auto px-6 py-10">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="w-10 h-10 grid place-items-center -ml-2 hover:bg-[#F5F5F5] rounded-lg">
          <ArrowLeft size={20} />
        </button>

        <div className="mt-6"><Logo /></div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-8">Welcome Back</h1>
        <p className="text-[15px] mt-2 leading-relaxed" style={{ color: "#5A5A5A" }}>Sign in to submit and track your referrals.</p>

        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          <div>
            <label className="label-up">Email</label>
            <input data-testid="login-email-input" className="input-field" type="email" placeholder="you@agency.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} />
          </div>
          <div>
            <label className="label-up">Password</label>
            <div className="relative">
              <input data-testid="login-password-input" className="input-field pr-12" type={showPwd ? "text" : "password"} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} />
              <button data-testid="toggle-pwd-btn" type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5">
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="flex items-start gap-2 p-3 rounded-xl status-declined">
              <AlertCircle size={16} className="mt-0.5" /> <span className="text-sm">{error}</span>
            </div>
          )}

          <button data-testid="login-submit-btn" type="submit" disabled={loading} className="btn-primary w-full !py-3.5">
            {loading ? <Loader2 className="animate-spin" size={18} /> : (<>Sign In <ArrowRight size={18} /></>)}
          </button>
        </form>

        <div className="flex items-center my-7">
          <div className="flex-1 h-px bg-[#E8E8E8]" />
          <span className="px-3 text-[11px] font-extrabold tracking-wider" style={{ color: "#8A8A8A" }}>OR</span>
          <div className="flex-1 h-px bg-[#E8E8E8]" />
        </div>

        <Link data-testid="go-register-btn" to="/register" className="btn-secondary w-full !py-3.5 block text-center">Create a Partner Account</Link>

        <Link data-testid="go-forgot-btn" to="/forgot-password" className="block text-center mt-6 underline font-bold text-sm" style={{ color: "#D62828" }}>
          Forgot your password?
        </Link>
      </div>
    </div>
  );
}
